import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, AlertCircle, CheckCircle, Building2, RefreshCw, Clock, Copy, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSongsContext } from './contexts/SongsContext';
import { EventSpacesEditor } from './components/EventSpaces';
import { EventScheduleNotesEditor } from './components/EventScheduleNotes';
import ClientTextImportDialog from './components/ClientTextImportDialog';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Servicios por defecto (fallback si no hay conexión a API)
const SERVICIOS_DEFAULT = [
  'Ceremonia',
  'Saxo en Cóctel',
  'DJ',
  'Fotomatón',
  '360',
  'CO2',
  'Smoke Gun',
  'Fuego Frío',
  'Dúo Musical',
  'Luces Especiales',
  'Maestro de Ceremonias',
];

const MONTAJES = [
  'Básico',
  'Madera',
  'Boombox',
  'Nuevo',
  'Reforzado'
];

export const PlaylistForm = ({ initialData, onSubmit, onCancel, submitLabel = "Crear", isAdmin = false }) => {
  // Get available genres from library
  const { availableGenres: libraryGenres } = useSongsContext();
  
  // State for services loaded from API
  const [serviciosDisponibles, setServiciosDisponibles] = useState([]);
  const [loadingServicios, setLoadingServicios] = useState(false);
  const [serviciosError, setServiciosError] = useState(null);

  // State for team members (DJs and assistants)
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(false);

  // Initialize servicios_contratados properly
  const initializeServicios = (data, availableServices = []) => {
    // If we have services from API, use those
    if (availableServices.length > 0) {
      const existingServicesMap = new Map();
      if (data?.servicios_contratados) {
        data.servicios_contratados.forEach(s => {
          existingServicesMap.set(s.servicio, s);
        });
      }
      
      return availableServices.map(servicio => {
        const existing = existingServicesMap.get(servicio.nombre_servicio);
        return {
          servicio: servicio.nombre_servicio,
          servicio_id: servicio.id,
          precio_base: servicio.precio_base,
          precio_personalizado: existing?.precio_personalizado ?? existing?.precio_final,
          categoria: servicio.categoria,
          descripcion: servicio.descripcion,
          venue_exclusivo: servicio.venue_exclusivo,
          contratado: existing?.contratado || false,
          cantidad: existing?.cantidad || 1,
          notas: existing?.notas || ''
        };
      });
    }
    
    // Fallback to existing data or default services
    if (data?.servicios_contratados && data.servicios_contratados.length > 0) {
      return data.servicios_contratados;
    }
    
    // Use default services as fallback
    return SERVICIOS_DEFAULT.map(s => ({
      servicio: s,
      contratado: false,
      notas: ''
    }));
  };

  const [formData, setFormData] = useState(initialData ? {
    ...initialData,
    numero_invitados: initialData.numero_invitados || '',
    cache_acordado: initialData.cache_acordado || '',
    playlist_spotify: initialData.playlist_spotify || '',
    playlist_spotify_urls: initialData.playlist_spotify_urls || [],
    fianza: initialData.fianza || false,
    hora_loca_cancion: initialData.hora_loca_cancion || '',
    duracion_baile: initialData.duracion_baile || '',
    tipo_evento: initialData.tipo_evento || '',
    nombre_dj: initialData.nombre_dj || '',
    dj_user_id: initialData.dj_user_id || '',
    nombre_asistente: initialData.nombre_asistente || '',
    asistente_user_id: initialData.asistente_user_id || '',
    nombre_asistente2: initialData.nombre_asistente2 || '',
    asistente2_user_id: initialData.asistente2_user_id || '',
    horario_montaje: initialData.horario_montaje || '',
    telefono_novios: initialData.telefono_novios || '',
    email_novios: initialData.email_novios || '',
    link_fotomaton: initialData.link_fotomaton || '',
    link_360: initialData.link_360 || '',
    gustos_musicales: initialData.gustos_musicales || '',
    canciones_prohibidas: initialData.canciones_prohibidas || '',
    canciones_imperdibles: initialData.canciones_imperdibles || '',
    porcentajes_estilos: initialData.porcentajes_estilos || '',
    client_notas: initialData.client_notas || '',
    venue_id: initialData.venue_id || '',
    espacios_evento: initialData.espacios_evento || [],
    horarios_momentos: initialData.horarios_momentos || [],
    observaciones_operativas: initialData.observaciones_operativas || [],
    servicios_contratados: initialData.servicios_contratados || []
  } : {
    nombre_novios: '',
    fecha_evento: '',
    lugar_evento: '',
    venue_id: '',
    espacios_evento: [],
    horarios_momentos: [],
    observaciones_operativas: [],
    horario_inicio: '',
    horario_fin: '',
    duracion_baile: '',
    tipo_evento: '',
    playlist_spotify: '',
    playlist_spotify_urls: [],
    fianza: false,
    hora_loca_cancion: '',
    nombre_dj: '',
    dj_user_id: '',
    nombre_asistente: '',
    asistente_user_id: '',
    nombre_asistente2: '',
    asistente2_user_id: '',
    horario_montaje: '',
    montaje_elegido: '',
    numero_invitados: '',
    telefono_novios: '',
    email_novios: '',
    cache_acordado: '',
    link_fotomaton: '',
    link_360: '',
    gustos_musicales: '',
    canciones_prohibidas: '',
    canciones_imperdibles: '',
    porcentajes_estilos: '',
    client_notas: '',
    servicios_contratados: [],
    estado: 'pendiente',
    notas: ''
  });

  // Procesar "Notas del Cliente" con IA (Texto DJ Master): el cliente a veces
  // pega listados de canciones ahí, y el DJ necesita repartirlos entre
  // imperdibles/vetos/momentos. Tras aplicar, se re-sincronizan solo los
  // campos que la IA puede tocar (son los mismos campos CSV que ya edita
  // este formulario), para no perder esos cambios si luego se pulsa
  // "Actualizar Playlist" con el resto de ediciones manuales ya en curso.
  const [showAiReview, setShowAiReview] = useState(false);
  const copyClientNotas = async () => {
    try {
      await navigator.clipboard.writeText(formData.client_notas || '');
      toast.success('Notas copiadas al portapapeles');
    } catch (e) {
      toast.error('No se pudo copiar');
    }
  };
  const handleAiReviewApplied = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/api/playlists/${initialData.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Si el admin ya había editado manualmente alguno de estos 4 campos sin
      // guardar antes de pulsar "Procesar con IA", ese cambio sin guardar se
      // sustituye por el valor del servidor (que ya incluye lo aplicado por
      // la IA) — se avisa para que no se pierda en silencio.
      const fieldsToSync = ['canciones_imperdibles', 'canciones_prohibidas', 'gustos_musicales', 'playlist_spotify_urls'];
      const overwritten = fieldsToSync.filter((f) => {
        const current = formData[f];
        const original = initialData[f] ?? (Array.isArray(current) ? [] : '');
        return JSON.stringify(current) !== JSON.stringify(original);
      });
      if (overwritten.length > 0) {
        toast.warning(`Había cambios sin guardar en "${overwritten.join(', ')}" que se reemplazaron por los datos actualizados por la IA`);
      }
      setFormData((prev) => ({
        ...prev,
        canciones_imperdibles: data.canciones_imperdibles || '',
        canciones_prohibidas: data.canciones_prohibidas || '',
        gustos_musicales: data.gustos_musicales || '',
        playlist_spotify_urls: data.playlist_spotify_urls || [],
      }));
    } catch (e) {
      console.error('Error re-sincronizando tras IA:', e);
    }
  };

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');

  // State for genre percentages table
  const [genrePercentages, setGenrePercentages] = useState([]);
  const [newGenre, setNewGenre] = useState('');
  const [newPercentage, setNewPercentage] = useState('');

  // Available genres - use library genres if available, otherwise fallback to common genres
  // FALLBACK_GENRES must match ALLOWED_GENRES from backend exactly
  // This ensures consistency between library classification and playlist distribution
  const FALLBACK_GENRES = [
    // Décadas
    "60s-70s", "80s", "90s", "2000s",
    // Latino  
    "Bachata", "Merengue", "Reggaeton", "Reggaeton clásico",
    "Electro latino", "Rumbas", "Cumbia", "Clasicos latinos",
    // Pop
    "Pop español", "Pop clásico español",
    // Rock
    "Rock español", "Rock clásico español", "Rock clásico internacional",
    // Electrónica/Dance
    "Dance clásico", "House", "Remember",
    // Urbano
    "Rap",
    // Especiales
    "Ambiente banquete", "Canción del verano", "Pachanga", "Humor", "Navidad",
    // Otros géneros
    "Baladas", "BOLERO", "Blues", "Indie español", "Soul RB", 
    "Valses", "Remixes", "Euskera", "catalan"
  ];
  
  // Use genres from library, add "Otros" at the end
  const AVAILABLE_GENRES = libraryGenres && libraryGenres.length > 0 
    ? [...libraryGenres.filter(g => g && g.trim()), 'Otros']
    : [...FALLBACK_GENRES, 'Otros'];

  // Load services from API based on venue
  // useFirstLoad tracks if this is the initial load (to use initialData)
  // or a subsequent load due to venue change (to preserve current state)
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  
  const loadServicios = useCallback(async (venue = '') => {
    setLoadingServicios(true);
    setServiciosError(null);
    
    try {
      const token = localStorage.getItem('token');
      const params = {};
      
      // If venue is specified, filter by it
      if (venue && venue.trim()) {
        params.venue = venue.trim();
      }
      
      const response = await axios.get(`${API}/api/precios`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      const precios = response.data.precios || [];
      
      // Filter only active services
      const activeServices = precios.filter(p => p.activo !== false);
      
      setServiciosDisponibles(activeServices);
      
      // Determine which services data to use for initialization:
      // - On first load: use initialData.servicios_contratados (from database)
      // - On venue change: use current formData.servicios_contratados (preserves user's edits)
      setFormData(prev => {
        let savedServices;
        if (isFirstLoad) {
          // First load: use data from database (initialData)
          savedServices = initialData?.servicios_contratados || [];
          setIsFirstLoad(false);
        } else {
          // Venue changed: preserve current state (user's edits)
          savedServices = prev.servicios_contratados || [];
        }
        
        const newServiciosContratados = initializeServicios({ servicios_contratados: savedServices }, activeServices);
        return {
          ...prev,
          servicios_contratados: newServiciosContratados
        };
      });
      
    } catch (error) {
      console.error('Error loading services:', error);
      setServiciosError('Error al cargar servicios');
      
      // Use default services as fallback
      const fallbackServices = initializeServicios(initialData || {}, []);
      setFormData(prev => ({
        ...prev,
        servicios_contratados: fallbackServices
      }));
    } finally {
      setLoadingServicios(false);
    }
  }, [initialData, isFirstLoad]);

  // Load services when component mounts or when lugar_evento changes
  useEffect(() => {
    loadServicios(formData.lugar_evento);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps: formData
  }, []);

  // Load team members (DJs and assistants) for the selectors
  const loadTeamMembers = useCallback(async () => {
    setLoadingTeam(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/users`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      // All users can be assigned as DJ or assistant
      setTeamMembers(response.data || []);
    } catch (error) {
      console.error('Error loading team members:', error);
      // Silently fail - the user can still type manually if needed
      setTeamMembers([]);
    } finally {
      setLoadingTeam(false);
    }
  }, []);

  // Load team members on mount
  useEffect(() => {
    loadTeamMembers();
  }, [loadTeamMembers]);

  // Handle team member selection - updates both name and ID
  const handleTeamMemberSelect = (field, userId) => {
    if (!userId) {
      // Clear selection
      if (field === 'dj') {
        setFormData(prev => ({ ...prev, nombre_dj: '', dj_user_id: '' }));
      } else if (field === 'asistente') {
        setFormData(prev => ({ ...prev, nombre_asistente: '', asistente_user_id: '' }));
      } else if (field === 'asistente2') {
        setFormData(prev => ({ ...prev, nombre_asistente2: '', asistente2_user_id: '' }));
      }
      return;
    }
    
    const member = teamMembers.find(m => m.id === userId);
    if (member) {
      // Use 'name' if available, otherwise use 'username'
      const displayName = member.name || member.username;
      if (field === 'dj') {
        setFormData(prev => ({ ...prev, nombre_dj: displayName, dj_user_id: member.id }));
      } else if (field === 'asistente') {
        setFormData(prev => ({ ...prev, nombre_asistente: displayName, asistente_user_id: member.id }));
      } else if (field === 'asistente2') {
        setFormData(prev => ({ ...prev, nombre_asistente2: displayName, asistente2_user_id: member.id }));
      }
    }
  };

  // Handle lugar_evento change - reload services
  const handleLugarEventoChange = (newLugar) => {
    setFormData({ ...formData, lugar_evento: newLugar });
    
    // Debounce the API call
    const timeoutId = setTimeout(() => {
      loadServicios(newLugar);
    }, 500);
    
    return () => clearTimeout(timeoutId);
  };

  // Helper: Parse porcentajes string to array
  const parsePercentages = (str) => {
    if (!str) return [];
    // Format: "30% Reggaeton, 20% Pop español" or "Reggaeton:30, Pop:20"
    const items = str.split(',').map(s => s.trim()).filter(Boolean);
    const result = [];
    
    for (const item of items) {
      // Try format "30% Género"
      let match = item.match(/(\d+)\s*%\s*(.+)/);
      if (match) {
        result.push({ genre: match[2].trim(), percentage: parseInt(match[1]) });
        continue;
      }
      // Try format "Género:30"
      match = item.match(/(.+):\s*(\d+)/);
      if (match) {
        result.push({ genre: match[1].trim(), percentage: parseInt(match[2]) });
      }
    }
    return result;
  };

  // Helper: Convert array to backend string format
  const serializePercentages = (arr) => {
    if (!arr || arr.length === 0) return '';
    return arr.map(item => `${item.percentage}% ${item.genre}`).join(', ');
  };

  // Initialize genre percentages from formData
  useEffect(() => {
    if (formData.porcentajes_estilos) {
      setGenrePercentages(parsePercentages(formData.porcentajes_estilos));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps: formData
  }, []);

  // Load templates on component mount (only for new events)
  useEffect(() => {
    if (!initialData) {
      const fetchTemplates = async () => {
        try {
          const response = await axios.get(`${API}/api/event-templates`);
          setTemplates(response.data);
        } catch (error) {
          console.error('Error loading templates:', error);
        }
      };
      fetchTemplates();
    }
  }, [initialData]);

  const applyTemplate = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (!template) return;

    setFormData({
      ...formData,
      duracion_baile: template.duracion_baile,
      gustos_musicales: template.gustos_musicales,
      porcentajes_estilos: template.porcentajes_estilos,
      numero_invitados: template.numero_invitados_tipico.toString()
    });
    
    setSelectedTemplate(templateId);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Filter only contracted services with complete data
    const serviciosFinales = formData.servicios_contratados
      .filter(s => s.contratado)
      .map(s => {
        const precioFinal = s.precio_personalizado ?? s.precio_base ?? 0;
        return {
          servicio: s.servicio,
          servicio_id: s.servicio_id,
          precio_base: s.precio_base,
          precio_personalizado: s.precio_personalizado,
          precio_final: precioFinal,
          categoria: s.categoria,
          contratado: true,
          cantidad: s.cantidad || 1,
          subtotal: precioFinal * (s.cantidad || 1),
          notas: s.notas || ''
        };
      });
    
    const dataToSubmit = {
      ...formData,
      numero_invitados: formData.numero_invitados ? parseInt(formData.numero_invitados) : null,
      cache_acordado: formData.cache_acordado ? parseFloat(formData.cache_acordado) : null,
      servicios_contratados: serviciosFinales,
      // Guardar el evento = confirmar los espacios (limpia el badge "sugeridos por IA")
      espacios_sugeridos_ia: false
    };

    // Recuerdo: al ACTIVAR (OFF→ON) la visibilidad para el cliente, abrir WhatsApp
    // automáticamente con un mensaje prerredactado + enlace al portal de los novios.
    const wasVisible = !!initialData?.recuerdo_visible_cliente;
    const nowVisible = !!formData.recuerdo_visible_cliente;
    if (!wasVisible && nowVisible) {
      const code = formData.client_access_code || initialData?.client_access_code;
      const telefono = (formData.telefono_novios || initialData?.telefono_novios || '').trim();
      if (!code) {
        toast.info('Recuerdo activado. Comparte el portal con el botón "Portal Cliente" para que los novios accedan al recuerdo.');
      } else if (!telefono) {
        const portalUrl = `${window.location.origin}/portal?code=${code}`;
        try {
          navigator.clipboard.writeText(portalUrl);
          toast.success('Recuerdo activado. Enlace del portal copiado (no hay teléfono de los novios para WhatsApp).');
        } catch {
          toast.info(`Recuerdo activado. Enlace del portal: ${portalUrl}`);
        }
      } else {
        const fechaTxt = formData.fecha_evento
          ? new Date(formData.fecha_evento).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })
          : '';
        const portalUrl = `${window.location.origin}/portal?code=${code}`;
        const msg = `¡Hola! Ya está disponible el *recuerdo musical* de vuestra boda${fechaTxt ? ` (${fechaTxt})` : ''}.\n\nPodéis descargar el PDF con todas las canciones que sonaron en vuestro día especial desde vuestro portal:\n${portalUrl}\n\n¡Gracias por dejarnos ser la banda sonora de vuestro día!`;
        const tel = telefono.replace(/\D/g, '');
        const telPref = tel.startsWith('34') ? tel : `34${tel}`;
        window.open(`https://wa.me/${telPref}?text=${encodeURIComponent(msg)}`, '_blank');
        toast.success('Abriendo WhatsApp para enviar el recuerdo a los novios...');
      }
    }

    onSubmit(dataToSubmit);
  };

  const toggleServicio = (servicioNombre) => {
    const newServicios = formData.servicios_contratados.map(s =>
      s.servicio === servicioNombre ? { ...s, contratado: !s.contratado, cantidad: s.cantidad || 1 } : s
    );
    setFormData({ ...formData, servicios_contratados: newServicios });
  };

  const updateServicioCantidad = (servicioNombre, cantidad) => {
    const newServicios = formData.servicios_contratados.map(s =>
      s.servicio === servicioNombre ? { ...s, cantidad: Math.max(1, parseInt(cantidad) || 1) } : s
    );
    setFormData({ ...formData, servicios_contratados: newServicios });
  };

  const updateServicioPrecio = (servicioNombre, precio) => {
    const newServicios = formData.servicios_contratados.map(s =>
      s.servicio === servicioNombre ? { ...s, precio_personalizado: Math.max(0, parseFloat(precio) || 0) } : s
    );
    setFormData({ ...formData, servicios_contratados: newServicios });
  };

  const updateServicioNotas = (servicioNombre, notas) => {
    const newServicios = formData.servicios_contratados.map(s =>
      s.servicio === servicioNombre ? { ...s, notas } : s
    );
    setFormData({ ...formData, servicios_contratados: newServicios });
  };

  const addPlaylistUrl = (label, url) => {
    if (!label.trim() || !url.trim()) {
      alert('Por favor completa la etiqueta y la URL');
      return false;
    }
    
    if (!url.includes('spotify.com')) {
      alert('Por favor ingresa una URL válida de Spotify');
      return false;
    }
    
    const newPlaylist = {
      id: Date.now().toString(),
      label: label.trim(),
      url: url.trim()
    };
    
    setFormData({
      ...formData,
      playlist_spotify_urls: [...(formData.playlist_spotify_urls || []), newPlaylist]
    });
    
    return true;
  };

  const removePlaylistUrl = (index) => {
    const newUrls = formData.playlist_spotify_urls.filter((_, i) => i !== index);
    setFormData({ ...formData, playlist_spotify_urls: newUrls });
  };

  // Group services by category for display
  const serviciosPorCategoria = formData.servicios_contratados.reduce((acc, servicio) => {
    const categoria = servicio.categoria || 'Otros';
    if (!acc[categoria]) {
      acc[categoria] = [];
    }
    acc[categoria].push(servicio);
    return acc;
  }, {});

  // Calculate total of selected services
  const calcularTotalServicios = () => {
    return formData.servicios_contratados
      .filter(s => s.contratado)
      .reduce((sum, s) => sum + (((s.precio_personalizado ?? s.precio_base) || 0) * (s.cantidad || 1)), 0);
  };

  // Apply services total to cache_acordado
  const aplicarTotalACacheAcordado = () => {
    const total = calcularTotalServicios();
    setFormData({ ...formData, cache_acordado: total.toFixed(2) });
  };

  // Check if current venue is Delicatto (case insensitive)
  const isDelicatto = formData.lugar_evento?.toLowerCase().includes('delicatto');

  return (
    <>
    <form onSubmit={handleSubmit} className="space-y-4 w-full max-w-full overflow-x-hidden">
      {/* Template Selector - Only for new events */}
      {!initialData && templates.length > 0 && (
        <Card className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 border-purple-700/50">
          <CardContent className="p-4">
            <Label className="text-white font-bold mb-3 block">✨ Plantillas de Evento</Label>
            <p className="text-slate-300 text-sm mb-3">Elige una plantilla para pre-configurar el evento:</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {templates.map(template => (
                <button
                  key={template.id}
                  type="button"
                  onClick={() => applyTemplate(template.id)}
                  className={`p-4 rounded-lg border-2 transition-all text-left ${
                    selectedTemplate === template.id
                      ? 'border-purple-500 bg-purple-600/30'
                      : 'border-slate-700 bg-slate-800 hover:border-slate-600'
                  }`}
                >
                  <div className="text-3xl mb-2">{template.icono}</div>
                  <div className="text-white font-bold text-sm">{template.nombre}</div>
                  <div className="text-slate-400 text-xs mt-1">{template.descripcion}</div>
                </button>
              ))}
            </div>
            {selectedTemplate && (
              <p className="text-green-400 text-sm mt-3">✓ Plantilla aplicada. Puedes modificar los campos según necesites.</p>
            )}
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="basico" className="w-full">
        <TabsList className="grid w-full grid-cols-3 bg-slate-700 text-xs sm:text-sm">
          <TabsTrigger value="basico" className="text-xs sm:text-sm">Básico</TabsTrigger>
          <TabsTrigger value="detalles" className="text-xs sm:text-sm">Detalles</TabsTrigger>
          <TabsTrigger value="servicios" className="text-xs sm:text-sm flex items-center gap-1">
            Servicios
            {isDelicatto && <Building2 className="w-3 h-3 text-purple-400" />}
          </TabsTrigger>
        </TabsList>

        {/* Tab Básico */}
        <TabsContent value="basico" className="space-y-3 w-full max-w-full">
          {/* Nombre de novios y Fecha */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="nombre_novios">Evento *</Label>
              <Input
                id="nombre_novios"
                className="bg-slate-900 border-slate-700 text-white w-full"
                value={formData.nombre_novios}
                onChange={(e) => setFormData({ ...formData, nombre_novios: e.target.value })}
                placeholder="Ej: Boda María & Carlos"
                required
              />
            </div>
            <div>
              <Label htmlFor="fecha_evento">Fecha del Evento</Label>
              <Input
                id="fecha_evento"
                type="date"
                className="bg-slate-900 border-slate-700 text-white w-full"
                value={formData.fecha_evento}
                onChange={(e) => setFormData({ ...formData, fecha_evento: e.target.value })}
              />
            </div>
          </div>

          {/* Lugar y Tipo de Evento */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="lugar_evento" className="flex items-center gap-2">
                Lugar del Evento
                {isDelicatto && (
                  <Badge className="bg-purple-600/50 text-purple-200 text-xs">
                    <Building2 className="w-3 h-3 mr-1" />
                    Servicios especiales
                  </Badge>
                )}
              </Label>
              <Input
                id="lugar_evento"
                className={`bg-slate-900 border-slate-700 text-white w-full ${
                  isDelicatto ? 'border-purple-500/50' : ''
                }`}
                value={formData.lugar_evento}
                onChange={(e) => handleLugarEventoChange(e.target.value)}
                placeholder="Ej: Finca El Romeral, Madrid"
              />
              {isDelicatto && (
                <p className="text-xs text-purple-300 mt-1">
                  🏢 Los servicios de la pestaña "Servicios" se actualizarán con las tarifas de Delicatto
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="tipo_evento">Tipo de Evento</Label>
              <Input
                id="tipo_evento"
                data-testid="tipo-evento-input"
                className="bg-slate-900 border-slate-700 text-white w-full"
                value={formData.tipo_evento}
                onChange={(e) => setFormData({ ...formData, tipo_evento: e.target.value })}
                placeholder="Ej: Boda rockera años 80, española y francesa, 40 años"
              />
              <p className="text-xs text-slate-400 mt-1">
                💡 Incluye géneros (rock, disco, latina), nacionalidades y edades para IA personalizada
              </p>
            </div>
          </div>

          {/* Espacios del evento (venue del catálogo + asignación por fase) */}
          <div className="bg-slate-800/40 border border-slate-700/60 rounded-lg p-3 space-y-2">
            <Label className="flex items-center gap-2 text-cyan-300">
              <Building2 className="w-4 h-4" /> Espacios del evento
            </Label>
            <p className="text-xs text-slate-400">
              Asigna qué espacio se usa en cada fase (ej: Cóctel en Jardín 2, Banquete en Salón 2, Discoteca en Discoteca 2).
            </p>
            <EventSpacesEditor
              espacios={formData.espacios_evento || []}
              onChange={(espacios_evento) => setFormData({ ...formData, espacios_evento })}
              venueId={formData.venue_id || ''}
              lugarEvento={formData.lugar_evento || ''}
              playlistId={initialData?.id || ''}
              onVenueChange={(venueId, venueObj) => {
                setFormData((prev) => ({
                  ...prev,
                  venue_id: venueId,
                  // Si el lugar está vacío, autocompleta con el nombre del venue
                  lugar_evento: prev.lugar_evento?.trim() ? prev.lugar_evento : (venueObj?.nombre || prev.lugar_evento),
                }));
              }}
            />
          </div>


          {/* Horarios por momento + Observaciones operativas (2026-10, Fase 1) */}
          <div className="bg-slate-800/40 border border-slate-700/60 rounded-lg p-3 space-y-2">
            <Label className="flex items-center gap-2 text-violet-300">
              <Clock className="w-4 h-4" /> Horarios por momento y observaciones operativas
            </Label>
            <p className="text-xs text-slate-400">
              Se rellenan automáticamente al analizar un documento o email del venue. También puedes editarlos a mano.
            </p>
            <EventScheduleNotesEditor
              horarios={formData.horarios_momentos || []}
              onChangeHorarios={(horarios_momentos) => setFormData({ ...formData, horarios_momentos })}
              observaciones={formData.observaciones_operativas || []}
              onChangeObservaciones={(observaciones_operativas) => setFormData({ ...formData, observaciones_operativas })}
            />
          </div>


          {/* Duración Baile */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="duracion_baile">Duración Baile/Horas</Label>
              <Input
                id="duracion_baile"
                className="bg-slate-900 border-slate-700 text-white w-full"
                value={formData.duracion_baile}
                onChange={(e) => setFormData({ ...formData, duracion_baile: e.target.value })}
                placeholder="Ej: 5 horas"
              />
            </div>
          </div>

          {/* Equipo: DJ y Asistentes */}
          <div className="space-y-3">
            {/* Primera fila: DJ y Asistente 1 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="nombre_dj">Nombre del DJ</Label>
                <select
                  id="nombre_dj"
                  className="w-full h-10 px-3 py-2 bg-slate-900 border border-slate-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                  value={formData.dj_user_id || ''}
                  onChange={(e) => handleTeamMemberSelect('dj', e.target.value)}
                  data-testid="select-dj"
                >
                  <option value="">-- Seleccionar DJ --</option>
                  {teamMembers.map(member => (
                    <option key={member.id} value={member.id}>
                      {member.name || member.username} {member.role === 'admin' ? '(Admin)' : ''}
                    </option>
                  ))}
                </select>
                {/* Show manual input if no team members loaded or for custom entry */}
                {formData.nombre_dj && !formData.dj_user_id && (
                  <p className="text-xs text-amber-400 mt-1">Valor actual: {formData.nombre_dj} (sin vincular)</p>
                )}
              </div>
              <div>
                <Label htmlFor="nombre_asistente">Asistente 1</Label>
                <select
                  id="nombre_asistente"
                  className="w-full h-10 px-3 py-2 bg-slate-900 border border-slate-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                  value={formData.asistente_user_id || ''}
                  onChange={(e) => handleTeamMemberSelect('asistente', e.target.value)}
                  data-testid="select-asistente1"
                >
                  <option value="">-- Seleccionar Asistente --</option>
                  {teamMembers.map(member => (
                    <option key={member.id} value={member.id}>
                      {member.name || member.username} {member.role === 'admin' ? '(Admin)' : ''}
                    </option>
                  ))}
                </select>
                {formData.nombre_asistente && !formData.asistente_user_id && (
                  <p className="text-xs text-amber-400 mt-1">Valor actual: {formData.nombre_asistente} (sin vincular)</p>
                )}
              </div>
            </div>
            {/* Segunda fila: Asistente 2 y Horario de montaje */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="nombre_asistente2">Asistente 2</Label>
                <select
                  id="nombre_asistente2"
                  className="w-full h-10 px-3 py-2 bg-slate-900 border border-slate-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                  value={formData.asistente2_user_id || ''}
                  onChange={(e) => handleTeamMemberSelect('asistente2', e.target.value)}
                  data-testid="select-asistente2"
                >
                  <option value="">-- Seleccionar Asistente 2 --</option>
                  {teamMembers.map(member => (
                    <option key={member.id} value={member.id}>
                      {member.name || member.username} {member.role === 'admin' ? '(Admin)' : ''}
                    </option>
                  ))}
                </select>
                {formData.nombre_asistente2 && !formData.asistente2_user_id && (
                  <p className="text-xs text-amber-400 mt-1">Valor actual: {formData.nombre_asistente2} (sin vincular)</p>
                )}
              </div>
              <div>
                <Label htmlFor="horario_montaje">Fecha y Hora de Montaje</Label>
                <Input
                  id="horario_montaje"
                  type="datetime-local"
                  className="bg-slate-900 border-slate-700 text-white w-full"
                  value={formData.horario_montaje || ''}
                  onChange={(e) => setFormData({ ...formData, horario_montaje: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* Hora Inicio y Hora Fin */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="horario_inicio">Hora Inicio</Label>
              <Input
                id="horario_inicio"
                type="time"
                className="bg-slate-900 border-slate-700 text-white w-full"
                value={formData.horario_inicio}
                onChange={(e) => setFormData({ ...formData, horario_inicio: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="horario_fin">Hora Fin</Label>
              <Input
                id="horario_fin"
                type="time"
                className="bg-slate-900 border-slate-700 text-white w-full"
                value={formData.horario_fin}
                onChange={(e) => setFormData({ ...formData, horario_fin: e.target.value })}
              />
            </div>
          </div>

          {/* Teléfono y Email */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="telefono_novios">Teléfono</Label>
              <Input
                id="telefono_novios"
                type="tel"
                className="bg-slate-900 border-slate-700 text-white w-full"
                value={formData.telefono_novios}
                onChange={(e) => setFormData({ ...formData, telefono_novios: e.target.value })}
                placeholder="666 777 888"
              />
            </div>
            <div>
              <Label htmlFor="email_novios">Email</Label>
              <Input
                id="email_novios"
                type="email"
                className="bg-slate-900 border-slate-700 text-white w-full"
                value={formData.email_novios}
                onChange={(e) => setFormData({ ...formData, email_novios: e.target.value })}
                placeholder="email@ejemplo.com"
              />
            </div>
          </div>

          {/* Playlists de Spotify - Múltiples */}
          <div className="border border-slate-700 rounded-lg p-4 bg-slate-800/50">
            <Label className="text-white text-base mb-2 block">🎵 Playlists de Spotify del Cliente</Label>
            <p className="text-xs text-slate-400 mb-3">
              Añade una o varias playlists de Spotify que el cliente te haya compartido
            </p>
            
            {/* Lista de playlists existentes */}
            {formData.playlist_spotify_urls && formData.playlist_spotify_urls.length > 0 && (
              <div className="space-y-2 mb-3">
                {formData.playlist_spotify_urls.map((item, index) => (
                  <div key={item.id || index} className="flex items-center gap-2 bg-slate-900 p-2 rounded border border-slate-700">
                    <span className="bg-cyan-600/30 text-cyan-300 px-2 py-1 rounded text-xs font-semibold whitespace-nowrap">
                      {item.label}
                    </span>
                    <a 
                      href={item.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-green-400 hover:text-green-300 text-xs flex-1 truncate"
                    >
                      {item.url}
                    </a>
                    <button
                      type="button"
                      onClick={() => removePlaylistUrl(index)}
                      className="text-red-400 hover:text-red-300 p-1 rounded hover:bg-red-900/20"
                      title="Eliminar playlist"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Formulario para añadir nueva playlist */}
            <div className="space-y-2">
              <Input
                type="text"
                className="bg-slate-900 border-slate-700 text-white text-sm"
                placeholder="Etiqueta (ej: Música Cena, Baile, Favoritos)"
                id="new-playlist-label"
              />
              <Input
                type="text"
                className="bg-slate-900 border-slate-700 text-white text-sm"
                placeholder="https://open.spotify.com/playlist/..."
                id="new-playlist-url"
              />
              <button
                type="button"
                onClick={() => {
                  const label = document.getElementById('new-playlist-label').value;
                  const url = document.getElementById('new-playlist-url').value;
                  
                  if (addPlaylistUrl(label, url)) {
                    // Limpiar campos
                    document.getElementById('new-playlist-label').value = '';
                    document.getElementById('new-playlist-url').value = '';
                  }
                }}
                className="w-full bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded text-sm transition-colors"
              >
                + Añadir Playlist
              </button>
            </div>

            {/* Campo legacy para compatibilidad con eventos antiguos */}
            {formData.playlist_spotify && (
              <div className="mt-3 pt-3 border-t border-slate-700">
                <Label className="text-slate-400 text-xs">Playlist antigua (solo lectura)</Label>
                <p className="text-xs text-slate-500 mt-1 break-all">{formData.playlist_spotify}</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Tab Detalles */}
        <TabsContent value="detalles" className="space-y-3">
          <div>
            <Label htmlFor="montaje_elegido">Montaje Elegido</Label>
            <select
              id="montaje_elegido"
              className="w-full bg-slate-900 border-slate-700 text-white rounded px-3 py-2"
              value={formData.montaje_elegido}
              onChange={(e) => setFormData({ ...formData, montaje_elegido: e.target.value })}
            >
              <option value="">Seleccionar montaje</option>
              {MONTAJES.map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="numero_invitados">Número de Invitados</Label>
            <Input
              id="numero_invitados"
              type="number"
              className="bg-slate-900 border-slate-700 text-white w-full"
              value={formData.numero_invitados}
              onChange={(e) => setFormData({ ...formData, numero_invitados: e.target.value })}
              min="1"
            />
          </div>

          <div>
            <Label htmlFor="hora_loca_cancion">Canción de Inicio Hora Loca</Label>
            <Input
              id="hora_loca_cancion"
              className="bg-slate-900 border-slate-700 text-white w-full"
              value={formData.hora_loca_cancion}
              onChange={(e) => setFormData({ ...formData, hora_loca_cancion: e.target.value })}
              placeholder="Ej: Paquito el Chocolatero"
            />
            <p className="text-xs text-slate-400 mt-1">Canción especial para dar inicio a la hora loca</p>
          </div>

          {/* Fianza y Caché en la misma línea - Solo Admin */}
          {isAdmin && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-center space-x-2 bg-slate-900 p-3 rounded border border-slate-700">
                <input
                  type="checkbox"
                  id="fianza"
                  className="w-4 h-4"
                  checked={formData.fianza}
                  onChange={(e) => setFormData({ ...formData, fianza: e.target.checked })}
                />
                <Label htmlFor="fianza" className="cursor-pointer">
                  <span className="font-semibold text-cyan-300">Fianza Pagada</span>
                </Label>
              </div>
              
              <div>
                <Label htmlFor="cache_acordado" className="flex items-center gap-2 mb-1">
                  <span>Caché Acordado (€)</span>
                  <span className="text-xs bg-amber-600 px-2 py-0.5 rounded">SOLO ADMIN</span>
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="cache_acordado"
                    type="number"
                    step="0.01"
                    className="bg-slate-900 border-slate-700 text-white border-amber-500/50 flex-1"
                    value={formData.cache_acordado}
                    onChange={(e) => setFormData({ ...formData, cache_acordado: e.target.value })}
                    min="0"
                    placeholder="Caché confidencial"
                  />
                  {formData.servicios_contratados.filter(s => s.contratado).length > 0 && (
                    <button
                      type="button"
                      onClick={aplicarTotalACacheAcordado}
                      className="px-3 py-2 bg-cyan-600 hover:bg-cyan-700 text-white text-xs rounded whitespace-nowrap transition-colors"
                      title={`Usar total de servicios: ${calcularTotalServicios().toFixed(2)}€`}
                    >
                      📋 Usar Total ({calcularTotalServicios().toFixed(2)}€)
                    </button>
                  )}
                </div>
                {formData.servicios_contratados.filter(s => s.contratado).length > 0 && (
                  <p className="text-xs text-slate-400 mt-1">
                    Total servicios seleccionados: <span className="text-cyan-400 font-semibold">{calcularTotalServicios().toFixed(2)}€</span>
                  </p>
                )}
              </div>
            </div>
          )}

          <div>
            <Label htmlFor="estado">Estado del Evento</Label>
            <select
              id="estado"
              className="w-full bg-slate-900 border-slate-700 text-white rounded px-3 py-2"
              value={formData.estado}
              onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
            >
              <option value="pendiente">Pendiente</option>
              <option value="confirmado">Confirmado</option>
              <option value="completado">Completado</option>
            </select>
          </div>

          <div>
            <Label htmlFor="notas">Notas Generales</Label>
            <Textarea
              id="notas"
              className="bg-slate-900 border-slate-700 text-white min-h-[80px]"
              value={formData.notas}
              onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
              placeholder="Gustos musicales, preferencias especiales, observaciones..."
            />
          </div>

          <div>
            <Label htmlFor="recuerdo_mensaje" className="flex items-center gap-2">
              💛 Dedicatoria del Recuerdo
            </Label>
            <Textarea
              id="recuerdo_mensaje"
              data-testid="recuerdo-mensaje-input"
              className="bg-slate-900 border-slate-700 text-white min-h-[70px]"
              value={formData.recuerdo_mensaje || ''}
              onChange={(e) => setFormData({ ...formData, recuerdo_mensaje: e.target.value })}
              placeholder="Gracias por dejarnos ser la banda sonora de vuestro día."
            />
            <p className="text-xs text-slate-500 mt-1">
              Mensaje que aparecerá en el PDF recuerdo de los novios (disponible tras el evento). Si lo dejas vacío se usa el texto por defecto.
            </p>
          </div>

          <div className="flex items-center space-x-2 bg-slate-900 p-3 rounded border border-slate-700">
            <input
              type="checkbox"
              id="recuerdo_visible_cliente"
              data-testid="recuerdo-visible-cliente-toggle"
              className="w-4 h-4"
              checked={formData.recuerdo_visible_cliente || false}
              onChange={(e) => setFormData({ ...formData, recuerdo_visible_cliente: e.target.checked })}
            />
            <Label htmlFor="recuerdo_visible_cliente" className="cursor-pointer">
              <span className="font-semibold text-rose-300">💛 Mostrar recuerdo en el portal del cliente</span>
              <span className="block text-xs text-slate-500 font-normal">Actívalo solo cuando hayas revisado que las canciones reproducidas están bien marcadas.</span>
            </Label>
          </div>

          {/* Enlaces de descarga - Solo si servicios contratados */}
          {(formData.servicios_contratados.find(s => s.servicio?.includes('Fotomatón'))?.contratado || 
            formData.servicios_contratados.find(s => s.servicio?.includes('360'))?.contratado) && (
            <div className="border-t border-slate-600 pt-4 mt-4">
              <h4 className="text-sm font-semibold text-slate-300 mb-3">📸 Enlaces de Descarga para Clientes</h4>
              
              {formData.servicios_contratados.find(s => s.servicio?.includes('Fotomatón'))?.contratado && (
                <div className="mb-3">
                  <Label htmlFor="link_fotomaton" className="flex items-center gap-2">
                    <span>📷 Link Fotomatón</span>
                    {isAdmin && <span className="text-xs bg-cyan-600 px-2 py-0.5 rounded">Visible para clientes</span>}
                  </Label>
                  <Input
                    id="link_fotomaton"
                    type="url"
                    className="bg-slate-900 border-slate-700 text-white w-full"
                    value={formData.link_fotomaton}
                    onChange={(e) => setFormData({ ...formData, link_fotomaton: e.target.value })}
                    placeholder="https://..."
                  />
                  <p className="text-xs text-slate-400 mt-1">Los clientes verán este enlace en su portal</p>
                </div>
              )}
              
              {formData.servicios_contratados.find(s => s.servicio?.includes('360'))?.contratado && (
                <div>
                  <Label htmlFor="link_360" className="flex items-center gap-2">
                    <span>🎥 Link 360</span>
                    {isAdmin && <span className="text-xs bg-cyan-600 px-2 py-0.5 rounded">Visible para clientes</span>}
                  </Label>
                  <Input
                    id="link_360"
                    type="url"
                    className="bg-slate-900 border-slate-700 text-white w-full"
                    value={formData.link_360}
                    onChange={(e) => setFormData({ ...formData, link_360: e.target.value })}
                    placeholder="https://..."
                  />
                  <p className="text-xs text-slate-400 mt-1">Los clientes verán este enlace en su portal</p>
                </div>
              )}
            </div>
          )}

          <div className="border-t border-slate-600 pt-4 mt-4">
            <h3 className="text-cyan-400 font-semibold mb-3">Criterios para Lista Personalizada</h3>
            
            {/* Gustos musicales del cliente - EDITABLE */}
            <div className="mb-4 bg-gradient-to-r from-purple-900/30 to-pink-900/30 rounded-lg border border-purple-500/30 p-4">
              <Label className="text-purple-300 flex items-center gap-2 mb-2">
                <span>💜 Gustos Musicales del Cliente</span>
                {formData.gustos_musicales && formData.gustos_musicales.trim() && (
                  <span className="text-xs bg-purple-600/50 px-2 py-0.5 rounded">Desde Portal</span>
                )}
              </Label>
              <textarea
                value={formData.gustos_musicales || ''}
                onChange={(e) => setFormData({...formData, gustos_musicales: e.target.value})}
                placeholder="Ej: Pop español, Rock 80s, Reggaeton, Baladas románticas (separar por comas)"
                className="w-full bg-slate-800 border border-slate-600 text-white rounded-md p-3 text-sm min-h-[80px] resize-y focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
              />
              <p className="text-xs text-purple-300/70 mt-2">
                💡 Separa los géneros por comas. Estos se usarán como filtro inicial. Puedes refinar la distribución exacta con los porcentajes de abajo.
              </p>
            </div>
            
            {/* Genre Percentages Table */}
            <div className="mb-6">
              <Label className="mb-2 block">Distribución de Géneros Musicales</Label>
              <div className="bg-slate-900 rounded-lg border border-slate-700 p-4">
                {/* Existing Genres Table */}
                {genrePercentages.length > 0 && (
                  <div className="mb-4">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-700">
                          <th className="text-left text-slate-400 pb-2 px-2">Género</th>
                          <th className="text-left text-slate-400 pb-2 px-2 w-32">Porcentaje</th>
                          <th className="w-12"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {genrePercentages.map((item, index) => (
                          <tr key={index} className="border-b border-slate-800">
                            <td className="py-2 px-2 text-white">{item.genre}</td>
                            <td className="py-2 px-2">
                              <div className="flex items-center gap-2">
                                <Input
                                  type="number"
                                  min="0"
                                  max="100"
                                  value={item.percentage}
                                  onChange={(e) => {
                                    const newPercentages = [...genrePercentages];
                                    newPercentages[index].percentage = parseInt(e.target.value) || 0;
                                    setGenrePercentages(newPercentages);
                                    // Update formData - NO sobreescribir gustos_musicales
                                    setFormData({
                                      ...formData,
                                      porcentajes_estilos: serializePercentages(newPercentages)
                                    });
                                  }}
                                  className="w-20 bg-slate-800 border-slate-600 text-white"
                                />
                                <span className="text-slate-400">%</span>
                              </div>
                            </td>
                            <td className="py-2">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const newPercentages = genrePercentages.filter((_, i) => i !== index);
                                  setGenrePercentages(newPercentages);
                                  setFormData({
                                    ...formData,
                                    porcentajes_estilos: serializePercentages(newPercentages)
                                  });
                                }}
                                className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    
                    {/* Total Indicator */}
                    <div className="mt-3 flex items-center justify-between px-2 py-2 bg-slate-800/50 rounded">
                      <span className="text-white font-semibold">Total:</span>
                      <div className="flex items-center gap-2">
                        <span className={`font-bold text-lg ${
                          genrePercentages.reduce((sum, item) => sum + item.percentage, 0) === 100
                            ? 'text-green-400'
                            : 'text-yellow-400'
                        }`}>
                          {genrePercentages.reduce((sum, item) => sum + item.percentage, 0)}%
                        </span>
                        {genrePercentages.reduce((sum, item) => sum + item.percentage, 0) === 100 ? (
                          <CheckCircle className="w-5 h-5 text-green-400" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-yellow-400" />
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Add New Genre */}
                <div className="space-y-3">
                  <Label className="text-sm text-slate-300">Añadir Género</Label>
                  <div className="flex gap-2">
                    <select
                      value={newGenre}
                      onChange={(e) => setNewGenre(e.target.value)}
                      className="flex-1 bg-slate-800 border border-slate-600 text-white rounded-md p-2 text-sm"
                    >
                      <option value="">-- Seleccionar género --</option>
                      {AVAILABLE_GENRES.filter(g => !genrePercentages.find(gp => gp.genre === g)).map(genre => (
                        <option key={genre} value={genre}>{genre}</option>
                      ))}
                    </select>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={newPercentage}
                      onChange={(e) => setNewPercentage(e.target.value)}
                      placeholder="%"
                      className="w-24 bg-slate-800 border-slate-600 text-white"
                    />
                    <Button
                      type="button"
                      onClick={() => {
                        if (newGenre && newPercentage) {
                          const newItem = { genre: newGenre, percentage: parseInt(newPercentage) };
                          const newPercentages = [...genrePercentages, newItem];
                          setGenrePercentages(newPercentages);
                          setFormData({
                            ...formData,
                            porcentajes_estilos: serializePercentages(newPercentages)
                          });
                          setNewGenre('');
                          setNewPercentage('');
                        }
                      }}
                      disabled={!newGenre || !newPercentage}
                      className="bg-cyan-600 hover:bg-cyan-700"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-slate-400">
                    💡 Los géneros seleccionados deben sumar 100% para una distribución equilibrada
                  </p>
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="canciones_prohibidas">Canciones o Artistas Prohibidos (separados por comas)</Label>
              <Textarea
                id="canciones_prohibidas"
                className="bg-slate-900 border-slate-700 text-white min-h-[60px]"
                value={formData.canciones_prohibidas}
                onChange={(e) => setFormData({ ...formData, canciones_prohibidas: e.target.value })}
                placeholder="Ej: Despacito, Luis Fonsi, Macarena"
              />
              <p className="text-xs text-slate-400 mt-1">Canciones o artistas que NO deben sonar</p>
            </div>

            <div>
              <Label htmlFor="canciones_imperdibles">Canciones o Artistas Imperdibles (separados por comas)</Label>
              <Textarea
                id="canciones_imperdibles"
                className="bg-slate-900 border-slate-700 text-white min-h-[60px]"
                value={formData.canciones_imperdibles}
                onChange={(e) => setFormData({ ...formData, canciones_imperdibles: e.target.value })}
                placeholder="Ej: Bailando, Marc Anthony, Vivir mi vida"
              />
              <p className="text-xs text-slate-400 mt-1">Canciones o artistas que DEBEN estar</p>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="client_notas">Notas del Cliente</Label>
                <div className="flex gap-1.5">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={copyClientNotas}
                    disabled={!formData.client_notas}
                    className="h-7 px-2 text-xs border-slate-700 text-slate-300 hover:bg-slate-800 disabled:bg-slate-800 disabled:text-slate-500 disabled:border-slate-700 disabled:opacity-60"
                    data-testid="copy-client-notas-btn"
                  >
                    <Copy className="w-3.5 h-3.5 mr-1" />
                    Copiar
                  </Button>
                  {initialData?.id && (
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => setShowAiReview(true)}
                      disabled={!formData.client_notas}
                      className="h-7 px-2 text-xs bg-emerald-600 hover:bg-emerald-700"
                      data-testid="process-client-notas-ai-btn"
                    >
                      <Sparkles className="w-3.5 h-3.5 mr-1" />
                      Procesar con IA
                    </Button>
                  )}
                </div>
              </div>
              <Textarea
                id="client_notas"
                data-testid="client-notas-textarea"
                className="bg-slate-900 border-slate-700 text-white min-h-[100px]"
                value={formData.client_notas}
                onChange={(e) => setFormData({ ...formData, client_notas: e.target.value })}
                placeholder="Notas y comentarios escritos por el cliente desde el portal..."
              />
              <p className="text-xs text-slate-400 mt-1">
                📝 Normalmente lo escribe el cliente desde su portal, pero ahora también es editable aquí. Si pegó un
                listado de canciones, usa "Procesar con IA" para repartirlas entre imperdibles, vetos o momentos.
              </p>
            </div>
          </div>
        </TabsContent>

        {/* Tab Servicios - Dynamic from API */}
        <TabsContent value="servicios" className="space-y-3">
          {/* Header with venue info */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <Label className="text-lg">Servicios Contratados</Label>
              {isDelicatto ? (
                <p className="text-xs text-purple-300 mt-1 flex items-center gap-1">
                  <Building2 className="w-3 h-3" />
                  Mostrando tarifas especiales de Delicatto + servicios generales
                </p>
              ) : (
                <p className="text-xs text-slate-400 mt-1">
                  Marca los servicios que han contratado los clientes
                </p>
              )}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => loadServicios(formData.lugar_evento)}
              disabled={loadingServicios}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loadingServicios ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
          </div>

          {/* Loading state */}
          {loadingServicios && (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 animate-spin text-cyan-400 mx-auto mb-2" />
              <p className="text-slate-400">Cargando servicios...</p>
            </div>
          )}

          {/* Error state */}
          {serviciosError && (
            <div className="bg-red-900/30 border border-red-700 rounded-lg p-4 text-center">
              <AlertCircle className="w-6 h-6 text-red-400 mx-auto mb-2" />
              <p className="text-red-300">{serviciosError}</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => loadServicios(formData.lugar_evento)}
                className="mt-2 border-red-600 text-red-300 hover:bg-red-900/30"
              >
                Reintentar
              </Button>
            </div>
          )}

          {/* Services grouped by category */}
          {!loadingServicios && !serviciosError && (
            <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2">
              {Object.entries(serviciosPorCategoria)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([categoria, servicios]) => (
                <div key={categoria} className="space-y-2">
                  <div className="flex items-center gap-2 sticky top-0 bg-slate-800 py-2 z-10">
                    <h4 className="text-sm font-semibold text-cyan-400">{categoria}</h4>
                    <Badge variant="outline" className="text-xs border-slate-600 text-slate-400">
                      {servicios.length} servicios
                    </Badge>
                    {servicios.some(s => s.venue_exclusivo) && (
                      <Badge className="bg-purple-600/30 text-purple-300 text-xs">
                        <Building2 className="w-3 h-3 mr-1" />
                        Delicatto
                      </Badge>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 gap-2">
                    {servicios.map((servicio) => (
                      <Card 
                        key={servicio.servicio} 
                        className={`bg-slate-900 border-slate-700 transition-all ${
                          servicio.contratado 
                            ? 'border-cyan-500/50 bg-cyan-900/20' 
                            : 'hover:border-slate-600'
                        } ${
                          servicio.venue_exclusivo 
                            ? 'border-l-2 border-l-purple-500' 
                            : ''
                        }`}
                      >
                        <CardContent className="p-3">
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              id={`servicio-${servicio.servicio}`}
                              checked={servicio.contratado}
                              onChange={() => toggleServicio(servicio.servicio)}
                              className="mt-1 w-4 h-4 rounded border-slate-600 text-cyan-500 focus:ring-cyan-500"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2 flex-wrap">
                                <label
                                  htmlFor={`servicio-${servicio.servicio}`}
                                  className={`font-medium cursor-pointer text-sm ${
                                    servicio.contratado ? 'text-cyan-300' : 'text-slate-300'
                                  }`}
                                >
                                  {servicio.venue_exclusivo && (
                                    <Building2 className="w-3 h-3 inline mr-1 text-purple-400" />
                                  )}
                                  {servicio.servicio}
                                </label>
                                <div className="flex items-center gap-2">
                                  {servicio.precio_base !== undefined && (
                                    <span className={`text-sm ${
                                      servicio.contratado ? 'text-slate-400' : 'text-slate-500'
                                    }`}>
                                      {servicio.precio_base}€/ud
                                    </span>
                                  )}
                                </div>
                              </div>
                              
                              {servicio.descripcion && (
                                <p className="text-xs text-slate-500 mt-1 line-clamp-1">
                                  {servicio.descripcion}
                                </p>
                              )}
                              
                              {servicio.contratado && (
                                <div className="mt-3 space-y-2">
                                  {/* Precio, Cantidad y Subtotal */}
                                  <div className="flex items-center gap-3 bg-slate-800/50 p-2 rounded flex-wrap">
                                    <div className="flex items-center gap-2">
                                      <label className="text-xs text-slate-400">Precio:</label>
                                      <div className="relative">
                                        <Input
                                          type="number"
                                          step="0.01"
                                          min="0"
                                          value={servicio.precio_personalizado ?? servicio.precio_base ?? 0}
                                          onChange={(e) => updateServicioPrecio(servicio.servicio, e.target.value)}
                                          className="w-20 h-8 bg-slate-700 border-slate-600 text-white text-sm text-center pr-6"
                                        />
                                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">€</span>
                                      </div>
                                      {servicio.precio_personalizado !== undefined && 
                                       servicio.precio_personalizado !== servicio.precio_base && (
                                        <span className="text-xs text-amber-400" title="Precio modificado">✏️</span>
                                      )}
                                    </div>
                                    <span className="text-slate-500">×</span>
                                    <div className="flex items-center gap-2">
                                      <label className="text-xs text-slate-400">Cant:</label>
                                      <Input
                                        type="number"
                                        min="1"
                                        value={servicio.cantidad || 1}
                                        onChange={(e) => updateServicioCantidad(servicio.servicio, e.target.value)}
                                        className="w-14 h-8 bg-slate-700 border-slate-600 text-white text-sm text-center"
                                      />
                                    </div>
                                    <div className="flex items-center gap-1 text-sm">
                                      <span className="text-slate-400">=</span>
                                      <span className="font-bold text-green-400">
                                        {(((servicio.precio_personalizado ?? servicio.precio_base) || 0) * (servicio.cantidad || 1)).toFixed(2)}€
                                      </span>
                                    </div>
                                  </div>
                                  
                                  {/* Notas */}
                                  <Input
                                    className="bg-slate-800 border-slate-600 text-white text-sm"
                                    placeholder="Notas adicionales..."
                                    value={servicio.notas}
                                    onChange={(e) => updateServicioNotas(servicio.servicio, e.target.value)}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
              
              {formData.servicios_contratados.length === 0 && (
                <div className="text-center py-8 text-slate-400">
                  <p>No hay servicios disponibles.</p>
                  <p className="text-sm mt-2">Prueba a actualizar o verificar la conexión.</p>
                </div>
              )}
            </div>
          )}

          {/* Summary of contracted services */}
          {formData.servicios_contratados.filter(s => s.contratado).length > 0 && (
            <div className="border-t border-slate-700 pt-4 mt-4">
              <div className="bg-slate-800/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-white">
                    ✅ Servicios seleccionados ({formData.servicios_contratados.filter(s => s.contratado).length})
                  </h4>
                  <div className="text-lg font-bold text-green-400">
                    Total: {formData.servicios_contratados
                      .filter(s => s.contratado)
                      .reduce((sum, s) => sum + (((s.precio_personalizado ?? s.precio_base) || 0) * (s.cantidad || 1)), 0)
                      .toFixed(2)}€
                  </div>
                </div>
                <div className="space-y-1">
                  {formData.servicios_contratados
                    .filter(s => s.contratado)
                    .map(s => {
                      const precioUsado = s.precio_personalizado ?? s.precio_base ?? 0;
                      const esModificado = s.precio_personalizado !== undefined && s.precio_personalizado !== s.precio_base;
                      return (
                        <div 
                          key={s.servicio} 
                          className={`flex items-center justify-between text-sm py-1 px-2 rounded ${
                            s.venue_exclusivo 
                              ? 'bg-purple-900/30 text-purple-200' 
                              : 'bg-cyan-900/30 text-cyan-200'
                          }`}
                        >
                          <span className="flex items-center gap-1">
                            {s.venue_exclusivo && <Building2 className="w-3 h-3" />}
                            {s.servicio}
                            {(s.cantidad || 1) > 1 && (
                              <span className="text-xs text-slate-400 ml-1">
                                (x{s.cantidad})
                              </span>
                            )}
                            {esModificado && (
                              <span className="text-xs text-amber-400 ml-1" title={`Precio original: ${s.precio_base}€`}>
                                ✏️
                              </span>
                            )}
                          </span>
                          <span className="font-semibold">
                            {(precioUsado * (s.cantidad || 1)).toFixed(2)}€
                          </span>
                        </div>
                      );
                    })
                  }
                </div>
                <p className="text-xs text-slate-400 mt-3">
                  💡 Estos servicios se añadirán al presupuesto con el botón "Auto-rellenar desde Evento"
                </p>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <div className="flex gap-2 pt-4">
        <Button
          type="button"
          onClick={onCancel}
          variant="outline"
          className="flex-1 bg-slate-700 border-slate-600 text-white"
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white"
        >
          {submitLabel}
        </Button>
      </div>
    </form>
    {initialData?.id && (
      <ClientTextImportDialog
        open={showAiReview}
        onClose={() => setShowAiReview(false)}
        eventId={initialData.id}
        eventName={formData.nombre_novios}
        initialText={formData.client_notas}
        title="Procesar notas del cliente"
        onApplied={async () => {
          setShowAiReview(false);
          await handleAiReviewApplied();
        }}
      />
    )}
    </>
  );
};
